import { StoredTaskVitalsSchema } from '../lib/taskVitals'
import type { TaskLogEntry } from './TaskShell'

function gib(bytes: number | null): string {
  return bytes == null ? 'unavailable' : `${(bytes / 1024 ** 3).toFixed(2)} GiB`
}

export default function TaskVitalsSection({ logs }: { logs: TaskLogEntry[] }) {
  const samples = logs.filter(log => log.log_type === 'task-vitals').flatMap(log => {
    try {
      const parsed = StoredTaskVitalsSchema.safeParse(JSON.parse(log.message))
      return parsed.success ? [{ ...parsed.data, id: log.id, receivedAt: log.created_at }] : []
    } catch { return [] }
  }).sort((a, b) => b.receivedAt.localeCompare(a.receivedAt))
  const latest = samples[0]
  if (!latest) return null
  return (
    <details className="rounded-lg border p-4" data-testid="task-vitals">
      <summary className="cursor-pointer font-medium">
        Task vitals · last memory {gib(latest.memory.usageBytes)} / {gib(latest.memory.limitBytes)}
      </summary>
      <p className="my-2 text-sm text-muted-foreground">
        One report per sample: container/process memory, browser heap and DOM counters, and recent recorder
        counters. Roles are inferred; RSS includes shared pages and should not be summed. Heap counters
        are per target and can overlap. Recorder queue bytes are not resident RAM. No forced garbage collection.
      </p>
      <div className="max-h-96 overflow-auto">
        <table className="w-full text-left text-sm">
          <thead><tr><th>Sampled</th><th>Task / last action</th><th>Pod</th><th>Memory / limit</th><th>Memory diagnostics</th></tr></thead>
          <tbody>{samples.map(sample => (
            <tr key={sample.id} className="border-t align-top">
              <td className="py-2 pr-3 whitespace-nowrap" title={`Received ${sample.receivedAt}`}>{new Date(sample.sampledAt).toLocaleTimeString()}</td>
              <td className="py-2 pr-3">{sample.taskKind ?? 'task'} · {sample.phase}<br /><span className="text-xs">{sample.taskId}</span></td>
              <td className="py-2 pr-3">{sample.podName}</td>
              <td className="py-2 pr-3 whitespace-nowrap">{gib(sample.memory.usageBytes)} / {gib(sample.memory.limitBytes)}</td>
              <td className="py-2"><details>
                <summary className="cursor-pointer">{sample.processes.slice(0, 3).map(p => `${p.browserProcessType ?? p.role} (${p.pid}): ${gib(p.rssBytes)}`).join('; ') || 'unavailable'}</summary>
                <ul>{sample.processes.map(p => <li key={p.pid}>
                  {p.role}{p.browserProcessType ? ` / ${p.browserProcessType}` : ''} · PID {p.pid}, parent {p.parentPid} · RSS {gib(p.rssBytes)}
                  {p.rssAnonBytes !== undefined && <> · anonymous {gib(p.rssAnonBytes)} / file {gib(p.rssFileBytes ?? null)} / shared {gib(p.rssShmemBytes ?? null)}</>}
                </li>)}</ul>
                <span>{sample.processCount} processes observed; showing up to 32. OOM kills: {sample.memory.oomKills ?? 'unavailable'}.</span>
                {sample.diagnostics ? <>
                  <p className="mt-2 font-medium">Browser counters · collection {sample.diagnostics.durationMs} ms</p>
                  <p>{sample.diagnostics.browserCount} browser processes; inspecting up to 4, with up to 12 targets each. Target IDs are not renderer PIDs.</p>
                  {sample.diagnostics.browsers.map(browser => <div key={browser.browserPid} className="mt-2">
                    <p>Browser PID {browser.browserPid}: {browser.status} · {browser.targetCount ?? 'unknown'} targets · {browser.durationMs} ms</p>
                    <ul>{browser.targets.map(target => <li key={target.targetId}>
                      {target.type} {target.targetId}: heap {target.heapStatus}
                      {target.heapStatus === 'ok' && <> · used {gib(target.heapUsedBytes)} / allocated {gib(target.heapTotalBytes)} · backing storage {gib(target.backingStorageBytes)} · embedder {gib(target.embedderHeapUsedBytes)}</>}
                      {'; '}DOM {target.domStatus}
                      {target.domStatus === 'ok' && <> · {target.documents} documents / {target.nodes} nodes / {target.listeners} listeners</>}
                    </li>)}</ul>
                  </div>)}
                  <p className="mt-2 font-medium">Recorder counters: {sample.diagnostics.recorderStatus}</p>
                  <p>Latest available counters per PID from up to 64 recorder log lines received in the preceding 30 seconds.</p>
                  <ul>{sample.diagnostics.recorders.map(recorder => <li key={recorder.pid}>
                    PID {recorder.pid}: queued {gib(recorder.queuedRecordingBytes)} · {recorder.checkpoints} checkpoints / {recorder.paints} paints · received {new Date(recorder.observedAt).toLocaleTimeString()}
                  </li>)}</ul>
                </> : <p className="mt-2">Browser and recorder diagnostics were not collected by this older sampler.</p>}
              </details></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </details>
  )
}
